import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { useWebsiteBuilderDebouncedUpdate, WebsiteBuilderDebouncedUpdateTime } from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import {
  IWebsiteBuilderSectionBackground,
  TWebsiteBuilderSection,
  TWebsiteBuilderSectionBackgroundMediaType,
  TWebsiteBuilderSectionBackgroundSize,
  isWebsiteBuilderMainSection,
  WEBSITE_BUILDER_SECTION_PARALLAX_MAX_AMOUNT,
  WEBSITE_BUILDER_SECTION_PARALLAX_MIN_AMOUNT,
} from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import React, { useMemo, useState } from "react";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import { CustomColorPickerPopover } from "components/colorpicker";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { useTranslation } from "react-i18next";
import { websiteBuilderUpdateHeader, websiteBuilderUpdateSectionBackground } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { SectionSettingsBackgroundMedia } from "./SectionSettingsBackgroundMedia";
import { IWebsiteBuilderSelectOption, renderWebsiteBuilderSelectOptions } from "../../../helpers/renderWebsiteBuilderSelectOptions";
import { Select } from "components/select/index";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import Checkbox from "components/form/checkbox/Checkbox";
import { IWebsiteBuilderHeader, isWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { websiteBuilderApplyHeaderPreset } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeaderPreset";
import { HeaderPresetPicker } from "./HeaderPresetPicker";

interface ISectionSettingsProps {
  node: TWebsiteBuilderSection;
  closeSettings: () => void;
}

export function SectionSettings(props: ISectionSettingsProps) {
  const { node, closeSettings } = props;
  
  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();
  
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  
  const id = node.id;
  const background = node.section.background;

  const [updatedBackground, setUpdatedBackground] = useState<IWebsiteBuilderSectionBackground>(() => cloneDeep(background));

  const backgroundSizeOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderSectionBackgroundSize>[] = useMemo(() => {
    return [
      {
        label: t("Cover"),
        value: "cover",
      },
      {
        label: t("Contain"),
        value: "contain",
      },
    ];
  }, []);

  function dispatchBackground(prevSectionBackground: IWebsiteBuilderSectionBackground, nextSectionBackground: IWebsiteBuilderSectionBackground) {
    dispatch(
      websiteBuilderUpdateSectionBackground(activePageId, deviceView, id, {
        id,
        prevSectionBackground: cloneDeep(prevSectionBackground),
        nextSectionBackground: cloneDeep(nextSectionBackground),
      }),
    );
  }

  function dispatchHeader(prevHeader: IWebsiteBuilderHeader, nextHeader: IWebsiteBuilderHeader) {
    dispatch(
      websiteBuilderUpdateHeader(activePageId, deviceView, id, {
        prevHeader: cloneDeep(prevHeader),
        nextHeader: cloneDeep(nextHeader),
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: background,
    updatedState: updatedBackground,
    setUpdatedState: setUpdatedBackground,
    dispatchState: dispatchBackground,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function handleUpdateBackgroundMedia(mediaId: number, src: string, mediaType: TWebsiteBuilderSectionBackgroundMediaType) {
    handleUpdateState(prev => ({
      ...prev,
      mediaId,
      src,
      mediaType,
    }));
  }

  function renderBackgroundColorSection() {
    return (
      <>
        <CustomColorPickerPopover
          title={t("Background")}
          hexColor={updatedBackground.color}
          updateHexColor={color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <Checkbox
          size="medium"
          label={t("Darken")}
          checked={updatedBackground.darken}
          onChange={e =>
            handleUpdateState(prev => ({
              ...prev,
              darken: e.target.checked,
            }))
          }
        />
        {updatedBackground.darken && (
          <WebsiteBuilderRange
            label={t("Darken percent")}
            min={0}
            max={100}
            step={1}
            value={updatedBackground.darkenPercent}
            setValue={darkenPercent =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                darkenPercent,
              }))
            }
          />
        )}
      </>
    );
  }

  function renderBackgroundMediaSection() {
    return (
      <>
        <SectionSettingsBackgroundMedia
          mediaId={updatedBackground.mediaId}
          src={updatedBackground.src}
          mediaType={updatedBackground.mediaType}
          handleUpdateBackgroundMedia={handleUpdateBackgroundMedia}
        />
        {updatedBackground.src && updatedBackground.mediaType !== "video" && (
          <>
            <Select
              label={t("Background size")}
              onChange={(size: TWebsiteBuilderSectionBackgroundSize) =>
                handleUpdateState(prev => ({
                  ...prev,
                  size,
                }))
              }
              defaultValue={updatedBackground.size}
            >
              {renderWebsiteBuilderSelectOptions(backgroundSizeOptions)}
            </Select>
            <WebsiteBuilderRange
              label={t("Background horizontal position (%)")}
              min={0}
              max={100}
              step={1}
              value={updatedBackground.xPercent}
              setValue={xPercent =>
                handleDebouncedUpdateState(prev => ({
                  ...prev,
                  xPercent,
                }))
              }
            />
            <WebsiteBuilderRange
              label={t("Background vertical position (%)")}
              min={0}
              max={100}
              step={1}
              value={updatedBackground.yPercent}
              setValue={yPercent =>
                handleDebouncedUpdateState(prev => ({
                  ...prev,
                  yPercent,
                }))
              }
            />
            {isWebsiteBuilderMainSection(node) && updatedBackground.size === "cover" && (
              <>
                <div className="website-builder-section-settings-parallax">
                  <Checkbox
                    size="medium"
                    label={t("Parallax")}
                    checked={updatedBackground.parallax}
                    onChange={e =>
                      handleUpdateState(prev => ({
                        ...prev,
                        parallax: e.target.checked,
                      }))
                    }
                  />
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 500, hide: 100 }}
                    overlay={
                      <Tooltip id="website-builder-section-parallax-help">
                        {t("The photo moves as the page scrolls in modern browsers. Firefox and older browsers show the still photo.")}
                      </Tooltip>
                    }
                  >
                    <span className="website-builder-section-settings-parallax-experimental">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      {t("Experimental")}
                    </span>
                  </OverlayTrigger>
                </div>
                {updatedBackground.parallax && (
                  <WebsiteBuilderRange
                    label={t("Amount")}
                    min={WEBSITE_BUILDER_SECTION_PARALLAX_MIN_AMOUNT}
                    max={WEBSITE_BUILDER_SECTION_PARALLAX_MAX_AMOUNT}
                    step={1}
                    value={updatedBackground.parallaxAmount}
                    setValue={parallaxAmount =>
                      handleDebouncedUpdateState(prev => ({
                        ...prev,
                        parallaxAmount,
                      }))
                    }
                  />
                )}
              </>
            )}
          </>
        )}
      </>
    );
  }

  const sections = [renderBackgroundColorSection(), renderBackgroundMediaSection()];

  if (isWebsiteBuilderHeader(node)) {
    sections.unshift(
      <HeaderPresetPicker onApply={preset => dispatchHeader(node, websiteBuilderApplyHeaderPreset(node, preset))} />,
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Section settings")}
      closePanel={closeSettings}
      sections={sections}
    />
  );
}